import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Phone, MapPin, Star, ArrowRight, Smartphone, Wifi, ArrowUpCircle,
    Signal, Headphones, LifeBuoy, ShieldCheck, HandHeart, Award, Users, ChevronRight
} from "lucide-react";
import { api } from "../lib/api";
import { BUSINESS } from "../data/site";
import HeroPhone from "../components/HeroPhone";
import ReviewCard from "../components/ReviewCard";
import ProductCard from "../components/ProductCard";
import ProductDetailModal from "../components/ProductDetailModal";

const ICONS = { Smartphone, Wifi, ArrowUpCircle, Signal, Headphones, LifeBuoy };

const fade = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

export default function Home() {
    const [services, setServices] = useState([]);
    const [products, setProducts] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [active, setActive] = useState(null);

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data));
        api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data.slice(0, 6)));
        api.get("/reviews", { params: { featured: true } }).then((r) => setReviews(r.data));
    }, []);

    return (
        <>
            {/* HERO */}
            <section data-testid="home-hero" className="relative overflow-hidden">
                <div className="hero-glow" />
                <div className="absolute inset-0 dot-grid opacity-40" />
                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial="hidden" animate="show" variants={fade}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 premium-shadow"
                        >
                            <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs font-semibold text-slate-700">{BUSINESS.rating} · {BUSINESS.reviewCount}+ Glendale reviews</span>
                        </motion.div>
                        <motion.h1
                            initial="hidden" animate="show" variants={fade} transition={{ delay: 0.1 }}
                            className="mt-5 font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
                        >
                            Your trusted <span className="gradient-text">wireless experts</span><br /> in Glendale.
                        </motion.h1>
                        <motion.p
                            initial="hidden" animate="show" variants={fade} transition={{ delay: 0.2 }}
                            className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
                        >
                            Premium smartphones, wireless plans, accessories, upgrades, activations and expert support — backed by outstanding, no-pressure customer service.
                        </motion.p>
                        <motion.div
                            initial="hidden" animate="show" variants={fade} transition={{ delay: 0.3 }}
                            className="mt-8 flex flex-col sm:flex-row gap-3"
                        >
                            <a
                                href={`tel:${BUSINESS.phoneRaw}`} data-testid="hero-call-btn"
                                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 transition"
                            >
                                <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
                            </a>
                            <a
                                href={BUSINESS.mapsDirections} target="_blank" rel="noreferrer" data-testid="hero-directions-btn"
                                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white border border-slate-200 text-slate-900 font-semibold hover:bg-slate-50 transition"
                            >
                                <MapPin className="w-4 h-4" /> Get Directions
                            </a>
                        </motion.div>
                        <motion.div
                            initial="hidden" animate="show" variants={fade} transition={{ delay: 0.4 }}
                            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4"
                        >
                            {[
                                ["4.8★", "Customer Rating"],
                                ["19+", "Verified Reviews"],
                                ["10 min", "Avg Activation"],
                                ["7 days", "Open Weekly"],
                            ].map(([n, l]) => (
                                <div key={l}>
                                    <div className="font-display font-bold text-2xl text-slate-900">{n}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                    <div className="lg:col-span-5 flex justify-center lg:justify-end">
                        <HeroPhone />
                    </div>
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section data-testid="home-services" className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">What we do</div>
                        <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight max-w-2xl">
                            Every wireless service, under one trusted roof.
                        </h2>
                    </div>
                    <Link to="/services" className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1 hover:gap-2 transition-all">
                        Browse all services <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {services.map((s, i) => {
                        const Icon = ICONS[s.icon] || Smartphone;
                        return (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                data-testid={`service-card-${i}`}
                                className="group relative bg-white rounded-3xl border border-slate-100 premium-shadow p-7 hover-lift"
                            >
                                <div className="h-12 w-12 rounded-2xl bg-slate-900 text-white grid place-items-center mb-5 group-hover:bg-blue-600 transition-colors">
                                    <Icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-display font-bold text-slate-900 text-xl">{s.name}</h3>
                                <p className="mt-2 text-slate-600 leading-relaxed text-sm">{s.short_description}</p>
                                <Link to="/services" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:gap-2 transition-all">
                                    Learn more <ChevronRight className="w-4 h-4" />
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* WHY CHOOSE */}
            <section data-testid="home-why" className="bg-slate-50">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Why EG Wireless</div>
                        <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight">
                            Honest advice. Real expertise. Glendale loyal.
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            We're not a big-box store. We're your neighbors — and we've earned a 4.8-star reputation by giving honest, unbiased recommendations and treating every customer like family.
                        </p>
                        <a href={`tel:${BUSINESS.phoneRaw}`} className="mt-8 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 transition">
                            <Phone className="w-4 h-4" /> Talk to a specialist
                        </a>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                            [HandHeart, "Exceptional Service", "We listen first, recommend second. Zero pressure."],
                            [Award, "Professional Expertise", "Years of hands-on smartphone & carrier knowledge."],
                            [ShieldCheck, "Honest Recommendations", "We'll tell you when you don't need to upgrade."],
                            [Users, "Community Trust", "Generations of Glendale customers keep returning."],
                        ].map(([Icon, t, d], i) => (
                            <motion.div
                                key={t}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.08 }}
                                className="bg-white rounded-2xl border border-slate-100 premium-shadow p-6"
                            >
                                <div className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-600 grid place-items-center mb-4">
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="font-display font-bold text-slate-900">{t}</div>
                                <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRODUCT SHOWCASE */}
            <section data-testid="home-products" className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Featured Products</div>
                        <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight max-w-2xl">
                            The latest devices and must-have accessories.
                        </h2>
                    </div>
                    <Link to="/products" className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1 hover:gap-2 transition-all">
                        See full catalog <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((p, i) => (
                        <ProductCard key={p.id} product={p} onOpen={setActive} dataTestId={`home-product-${i}`} />
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section data-testid="home-testimonials" className="bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-30" style={{
                    background: "radial-gradient(40rem 30rem at 80% 30%, rgba(37,99,235,0.35), transparent 60%), radial-gradient(35rem 25rem at 10% 70%, rgba(6,182,212,0.25), transparent 60%)",
                }} />
                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
                    <div className="max-w-2xl">
                        <div className="text-xs uppercase tracking-[0.22em] text-cyan-400 font-semibold">Loved by Glendale</div>
                        <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
                            Real reviews from real customers.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {reviews.slice(0, 3).map((r, i) => (
                            <div key={r.id} className="bg-white/5 border border-white/10 rounded-3xl p-7 backdrop-blur">
                                <div className="flex items-center gap-1 mb-4">
                                    {Array.from({ length: 5 }).map((_, j) => (
                                        <Star key={j} className={`w-4 h-4 ${j < r.rating ? "fill-yellow-400 text-yellow-400" : "text-white/20"}`} />
                                    ))}
                                </div>
                                <p className="text-white/90 leading-relaxed">&ldquo;{r.content}&rdquo;</p>
                                <div className="mt-6 flex items-center gap-3">
                                    {r.avatar && <img src={r.avatar} alt={r.name} className="h-10 w-10 rounded-full object-cover" />}
                                    <div>
                                        <div className="font-semibold text-sm">{r.name}</div>
                                        <div className="text-xs text-white/60">{r.location}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10">
                        <Link to="/reviews" className="inline-flex items-center gap-2 text-cyan-400 hover:text-white font-semibold transition">
                            Read all reviews <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ABOUT PREVIEW */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-6 order-2 lg:order-1">
                    <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">About EG Wireless</div>
                    <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl tracking-tight">
                        A neighborhood store with telecom-grade expertise.
                    </h2>
                    <p className="mt-5 text-slate-600 leading-relaxed">
                        For years, we've helped Glendale families, students, and professionals find the right phone, plan, and accessories — without the upsell games of big carriers. Personal service. Real conversations. Long-term relationships.
                    </p>
                    <Link to="/about" className="mt-7 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 transition">
                        Our Story <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="lg:col-span-6 order-1 lg:order-2">
                    <div className="relative rounded-3xl overflow-hidden premium-shadow">
                        <img
                            src="https://images.pexels.com/photos/11297769/pexels-photo-11297769.jpeg?auto=compress&cs=tinysrgb&w=1200"
                            alt="EG Wireless store"
                            className="w-full h-[420px] object-cover"
                        />
                        <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4 border-white/40">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Inside 7-Eleven Plaza</div>
                            <div className="font-display font-bold text-slate-900 mt-0.5">1105 S Glendale Ave</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LOCATION PREVIEW */}
            <section data-testid="home-location" className="bg-slate-50">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-stretch">
                    <div className="lg:col-span-7 rounded-3xl overflow-hidden premium-shadow">
                        <iframe
                            title="EG Wireless location"
                            src={BUSINESS.mapsEmbed}
                            className="w-full h-[420px] border-0"
                            loading="lazy"
                        />
                    </div>
                    <div className="lg:col-span-5 bg-white rounded-3xl border border-slate-100 premium-shadow p-8 flex flex-col">
                        <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Visit us</div>
                        <h3 className="mt-3 font-display font-bold text-2xl text-slate-900 tracking-tight">Stop by our Glendale store</h3>
                        <div className="mt-6 space-y-4 text-sm text-slate-700">
                            <div className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 mt-1 text-blue-600" />
                                <div>{BUSINESS.address}<br /><span className="text-slate-500">Inside {BUSINESS.locatedInside}</span></div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Phone className="w-4 h-4 mt-1 text-blue-600" />
                                <a href={`tel:${BUSINESS.phoneRaw}`} className="hover:text-slate-900">{BUSINESS.phone}</a>
                            </div>
                            <div className="border-t border-slate-100 pt-4">
                                <div className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-2">Hours</div>
                                <ul className="space-y-1 text-sm text-slate-600">
                                    <li className="flex justify-between"><span>Mon – Fri</span><span>12:00 PM – 5:00 PM</span></li>
                                    <li className="flex justify-between"><span>Saturday</span><span>12:00 PM – 4:00 PM</span></li>
                                    <li className="flex justify-between"><span>Sunday</span><span>12:00 PM – 4:00 PM</span></li>
                                </ul>
                            </div>
                        </div>
                        <div className="mt-auto pt-6 flex gap-3">
                            <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="location-preview-call" className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 transition">
                                <Phone className="w-4 h-4" /> Call
                            </a>
                            <a href={BUSINESS.mapsDirections} target="_blank" rel="noreferrer" data-testid="location-preview-directions" className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition">
                                <MapPin className="w-4 h-4" /> Directions
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <ProductDetailModal product={active} onClose={() => setActive(null)} />
        </>
    );
}
